/**
 * Vendor Bot Admin - Settings Page
 * Store configuration, payment gateways, shipping
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const Section = styled(Card)`
  margin-bottom: var(--spacing-md);
`;

const SectionTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-sm);
`;

const SettingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const SettingLabel = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-medium);
  color: var(--text-primary);
`;

const SettingDescription = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const Switch = styled.input.attrs({ type: 'checkbox' })`
  width: 48px;
  height: 28px;
  appearance: none;
  background: var(--border-color);
  border-radius: 14px;
  position: relative;
  cursor: pointer;
  transition: background var(--transition);

  &:checked {
    background: var(--accent-green);
  }

  &::before {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: 2px;
    transition: transform var(--transition);
  }

  &:checked::before {
    transform: translateX(20px);
  }
`;

const Settings: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { showBackButton } = useTelegram();
  const [settings, setSettings] = useState({
    storeActive: true,
    acceptOrders: true,
    requireAuth: true,
    enableReferrals: true,
    enableReviews: true,
  });

  useEffect(() => {
    showBackButton(() => navigate(`/vendor/${botUsername}/admin/dashboard`));
  }, [showBackButton, navigate, botUsername]);

  const handleToggle = (key: keyof typeof settings) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  return (
    <Layout header={<Navbar title="Settings" showBack />}>
      <Section>
        <SectionTitle>Store Settings</SectionTitle>
        <SettingRow>
          <SettingInfo>
            <SettingLabel>Store Active</SettingLabel>
            <SettingDescription>Enable/disable your store</SettingDescription>
          </SettingInfo>
          <Switch checked={settings.storeActive} onChange={() => handleToggle('storeActive')} />
        </SettingRow>
        <SettingRow>
          <SettingInfo>
            <SettingLabel>Accept Orders</SettingLabel>
            <SettingDescription>Allow customers to place orders</SettingDescription>
          </SettingInfo>
          <Switch checked={settings.acceptOrders} onChange={() => handleToggle('acceptOrders')} />
        </SettingRow>
      </Section>

      <Section>
        <SectionTitle>Security</SectionTitle>
        <SettingRow>
          <SettingInfo>
            <SettingLabel>Require Authentication</SettingLabel>
            <SettingDescription>Secret phrase + CAPTCHA</SettingDescription>
          </SettingInfo>
          <Switch checked={settings.requireAuth} onChange={() => handleToggle('requireAuth')} />
        </SettingRow>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <SettingRow>
          <SettingInfo>
            <SettingLabel>Referral System</SettingLabel>
            <SettingDescription>Multi-level referral rewards</SettingDescription>
          </SettingInfo>
          <Switch checked={settings.enableReferrals} onChange={() => handleToggle('enableReferrals')} />
        </SettingRow>
        <SettingRow>
          <SettingInfo>
            <SettingLabel>Product Reviews</SettingLabel>
            <SettingDescription>Allow customer reviews</SettingDescription>
          </SettingInfo>
          <Switch checked={settings.enableReviews} onChange={() => handleToggle('enableReviews')} />
        </SettingRow>
      </Section>

      <Button fullWidth>Save Changes</Button>
    </Layout>
  );
};

export default Settings;
